import { DateTime } from "luxon";
import type { InitialSchedule } from "./types";

type ScheduleTime = Pick<InitialSchedule, "date" | "hour" | "minute" | "period">;

const validZone = (timeZone: string): string | undefined =>
    timeZone && DateTime.local().setZone(timeZone).isValid ? timeZone : undefined;

// My Niche Video pre-fill (spec §4.6): now + 15 minutes, rounded up to the
// next quarter hour, as wall time in the agent's zone. getScheduledDateTime
// reads it back in that zone, and `date` carries that zone's calendar day.
export const nicheDefaultTime = (now: Date, timeZone: string): ScheduleTime => {
    const zone = validZone(timeZone);
    let dt = DateTime.fromJSDate(now, zone ? { zone } : {}).plus({ minutes: 15 }).startOf("minute");
    const rem = dt.minute % 15;
    if (rem) dt = dt.plus({ minutes: 15 - rem });
    return {
        date: new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute),
        hour: dt.hour % 12 || 12,
        minute: dt.minute,
        period: dt.hour >= 12 ? "PM" : "AM",
    };
};

export const seedNicheSchedules = (
    schedules: Record<string, InitialSchedule>,
    now: Date,
    timeZone: string,
): Record<string, InitialSchedule> => {
    const time = nicheDefaultTime(now, timeZone);
    return Object.fromEntries(
        Object.entries(schedules).map(([key, s]) => [key, { ...s, ...time, date: new Date(time.date) }]),
    );
};

// Seeds only the platforms not seeded yet and leaves the seeded (maybe edited) ones
// alone. `seeded` comes back as the keys present now; the wizard also forgets a platform
// when it is unchosen (forgetUnchosenSeeded), so adding it back seeds it again.
export const seedNewNicheSchedules = (
    schedules: Record<string, InitialSchedule>,
    seeded: ReadonlySet<string>,
    now: Date,
    timeZone: string,
): { schedules: Record<string, InitialSchedule>; seeded: Set<string> } => {
    const fresh = Object.fromEntries(Object.entries(schedules).filter(([key]) => !seeded.has(key)));
    const nowSeeded = new Set(Object.keys(schedules));
    if (Object.keys(fresh).length === 0) return { schedules, seeded: nowSeeded };
    return { schedules: { ...schedules, ...seedNicheSchedules(fresh, now, timeZone) }, seeded: nowSeeded };
};

export const forgetUnchosenSeeded = (
    seeded: ReadonlySet<string>,
    isChosen: (key: string) => boolean,
): ReadonlySet<string> => {
    const kept = Array.from(seeded).filter(isChosen);
    return kept.length === seeded.size ? seeded : new Set(kept);
};

// At Complete (spec §4.6): a time earlier than now + 2 minutes becomes
// now + 5, rounded up to the minute, instead of blocking the submit.
export const bumpNicheScheduledFor = (scheduledFor: string, now: Date): string => {
    const at = Date.parse(scheduledFor);
    if (!Number.isNaN(at) && at >= now.getTime() + 2 * 60_000) return scheduledFor;
    return new Date(Math.ceil((now.getTime() + 5 * 60_000) / 60_000) * 60_000).toISOString();
};
